import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import Select from '../common/Select';

const CreateSprint = ({ onClose, onSprintCreated }) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    goal: '',
    projectId: '',
    startDate: '',
    endDate: '',
    velocity: 0
  });

  useEffect(() => {
    const fetchProjects = async () => {
      if (!userProfile) return;

      try {
        const projectsQuery = query(
          collection(db, 'projects'),
          where('members', 'array-contains', userProfile.uid)
        );
        const snapshot = await getDocs(projectsQuery);
        const projectsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, [userProfile]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.projectId || !formData.startDate || !formData.endDate) {
      alert('Please fill in all required fields');
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    
    if (endDate <= startDate) {
      alert('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      
      const sprintData = {
        ...formData,
        status: 'planned',
        createdBy: userProfile.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        totalIssues: 0,
        completedIssues: 0,
        velocity: parseInt(formData.velocity) || 0
      };

      const docRef = await addDoc(collection(db, 'sprints'), sprintData);
      
      onSprintCreated({
        id: docRef.id,
        ...sprintData
      });
    } catch (error) {
      console.error('Error creating sprint:', error);
      alert('Failed to create sprint');
    } finally {
      setLoading(false);
    }
  };

  // Set default dates (next Monday to Friday)
  useEffect(() => {
    const today = new Date();
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7));
    const nextFriday = new Date(nextMonday);
    nextFriday.setDate(nextMonday.getDate() + 4);

    const formatDate = (date) => date.toISOString().split('T')[0];

    setFormData(prev => ({
      ...prev,
      startDate: formatDate(nextMonday),
      endDate: formatDate(nextFriday)
    }));
  }, []);

  return (
    <Modal
      title="Create New Sprint"
      onClose={onClose}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Sprint Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Sprint 1 - Feature Development"
          required
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sprint Goal
          </label>
          <textarea
            name="goal"
            value={formData.goal}
            onChange={handleChange}
            placeholder="What do you want to accomplish in this sprint?"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
            required
          />
        </div>

        <Select
          label="Project"
          name="projectId"
          value={formData.projectId}
          onChange={handleChange}
          options={projects.map(project => ({
            value: project.id,
            label: `${project.emoji} ${project.name}`
          }))}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Start Date"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
            required
          />
          
          <Input
            label="End Date"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={handleChange}
            required
          />
        </div>

        <Input
          label="Velocity (Story Points)"
          name="velocity"
          type="number"
          value={formData.velocity}
          onChange={handleChange}
          placeholder="Estimated velocity for this sprint"
          min="0"
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            Create Sprint
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSprint;